import { Component, Inject, OnInit } from '@angular/core';
import { formatDate } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { consts } from '../../../consts';
import { Utils } from '../../../shared/utils';
import { PagoCuotasFinanciacionPrivadaModel, FinanciacionPrivadaModel } from './financiacion-privada.model';
import { FinanciacionPrivadaService } from './financiacion-privada.service';
import { PreinvoiceService } from '../../invoice/preinvoice/preinvoice.service';
import { ActivityService } from '../../user/activity/activity.service';
import { MasterService } from '../../../modules_v2/master/services/master.service';
import { NotificationService } from '../../../modules_v2/shared/services/notification.service';
import { ConfirmService } from '../../../modules_v2/shared/services/confirm.service';
import { ConfirmDialogDetail } from '../../../modules_v2/shared/components/confirm-dialog.component';
import { stripFinancingConcept } from '../../../modules_v2/finance/financing-detail/financing-detail.model';
import {
  bankAccountOptions,
  currentActivityOption,
  EntityFormOption,
  fromApiDate,
  InjectedOptions,
  optionId,
} from '../../../modules_v2/finance/entity-form/entity-form.utils';

const NO_TAX_OPTION: EntityFormOption = { id: 0, display: 'Sin impuesto' };

@Component({
    selector: 'convfact-financiacionprivadapagocuotas',
    templateUrl: './financiacion-privada-dialog-pago-cuotas.component.html',
    styleUrls: ['./financiacion-dialogs.component.css'],
    standalone: false
})
export class FinanciacionPrivadaDialogPagoCuotasComponent implements OnInit {

  public financiacionPrivada: FinanciacionPrivadaModel;
  public editing: PagoCuotasFinanciacionPrivadaModel | null = null;

  public dataSource: PagoCuotasFinanciacionPrivadaModel[] = [];
  public bankAccounts: EntityFormOption[] = [];
  public activities: EntityFormOption[] = [];
  public taxes: EntityFormOption[] = [NO_TAX_OPTION];
  public pendingAmount = 0;

  public ready = false;
  public loadError = false;
  public saving = false;
  public savingMessage = '';
  public submitted = false;
  public saveError = '';

  public form: UntypedFormGroup;

  private model: PagoCuotasFinanciacionPrivadaModel = new PagoCuotasFinanciacionPrivadaModel();
  private changed = false;
  private requestedEditDone = false;
  private readonly injected = new InjectedOptions();

  constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<FinanciacionPrivadaDialogPagoCuotasComponent, boolean>,
    public financiacionPrivadaService: FinanciacionPrivadaService,
    private preinvoiceService: PreinvoiceService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public activityService: ActivityService,
    private masterService: MasterService,
    private notify: NotificationService,
    private confirm: ConfirmService,
  ) {
    this.financiacionPrivada = data.financiacionPrivada;
    this.pendingAmount = Number(this.financiacionPrivada.importePendiente) || 0;
    this.form = this.fb.group({
      fecha: [null],
      bankAccount: [null],
      concepto: [''],
      importeCapital: [null],
      intereses: [0],
      gastosBancarios: [0],
      tax: [NO_TAX_OPTION],
      activity: [null],
    });
  }

  get capitalPaid(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.importeCapital) || 0), 0));
  }

  get interestPaid(): number {
    return Utils.RoundTwoDecimals(this.dataSource.reduce((acc, item) => acc + (Number(item.intereses) || 0), 0));
  }

  get conceptoLength(): number {
    return String(this.form.get('concepto').value || '').length;
  }

  get missingDate(): boolean { return !this.form.get('fecha').value; }
  get missingConcept(): boolean { return !String(this.form.get('concepto').value || '').trim(); }
  get invalidImporteCapital(): boolean { return !Number(this.form.get('importeCapital').value); }
  get invalidIntereses(): boolean {
    const value = this.form.get('intereses').value;
    return value === null || value === undefined || value === '';
  }

  get missingActivity(): boolean { return optionId(this.form.get('activity').value) === null; }
  get missingBankAccount(): boolean { return optionId(this.form.get('bankAccount').value) === null; }

  public control(name: string): UntypedFormControl {
    return this.form.get(name) as UntypedFormControl;
  }

  public ngOnInit(): void {
    this.load();
  }

  public retry(): void {
    this.loadError = false;
    this.load();
  }

  public startEdit(item: PagoCuotasFinanciacionPrivadaModel): void {
    if (this.saving) return;
    this.editing = item;
    this.submitted = false;
    this.saveError = '';
    this.injected.clear();

    this.model = { ...item } as PagoCuotasFinanciacionPrivadaModel;

    this.form.patchValue({
      fecha: fromApiDate(item.fecha),
      bankAccount: this.injected.ensure(this.bankAccounts, item.bankAccountsUid, item.bankAccountInfo),
      concepto: this.itemConcept(item),
      importeCapital: item.importeCapital != null ? Number(item.importeCapital) : null,
      intereses: item.intereses != null ? Number(item.intereses) : null,
      gastosBancarios: item.gastosBancarios != null ? Number(item.gastosBancarios) : null,
      tax: this.findTax(item.taxesId),
      activity: this.injected.ensure(this.activities, item.activitiesUid, (item as any).activitiesName),
    }, { emitEvent: false });
  }

  public cancelEdit(): void {
    this.editing = null;
    this.resetForm();
  }

  public pay(): void {
    if (this.saving) return;
    this.submitted = true;
    this.saveError = '';
    if (this.missingDate || this.missingConcept || this.invalidImporteCapital || this.invalidIntereses || this.missingActivity || this.missingBankAccount) {
      return;
    }

    const isEdit = !!this.editing;
    const payload = this.buildPayload();

    const task = isEdit
      ? this.financiacionPrivadaService.pagoCoutasUpdate(payload)
      : this.financiacionPrivadaService.pagoCoutasCreate(payload);

    this.setSaving(true, isEdit ? 'Actualizando pago…' : 'Registrando pago…');
    task
      .then(() => {
        this.changed = true;
        this.setSaving(false);
        this.notify.ok(isEdit ? 'Pago actualizado correctamente' : 'Pago añadido correctamente');
        if (!isEdit || this.data.editUid) {
          this.dialogRef.close(true);
          return;
        }
        this.editing = null;
        this.resetForm();
        this.reloadList();
      })
      .catch(error => {
        this.setSaving(false);
        this.saveError = this.serverMessage(error) || (isEdit
          ? 'Ha ocurrido un error inesperado al actualizar el pago'
          : 'Ha ocurrido un error inesperado al añadir el pago');
      });
  }

  public delete(item: PagoCuotasFinanciacionPrivadaModel): void {
    if (this.saving) return;
    this.confirm.open({
      title: 'Eliminar pago de cuota',
      subtitle: this.financiacionPrivada.descripcion || undefined,
      message: 'Se eliminará el pago, se devolverá el capital al importe pendiente y se borrará su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
      tone: 'danger',
      details: this.itemDetails(item),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'Ha ocurrido un error al eliminar el pago',
      action: () => this.financiacionPrivadaService.pagoCoutasDelete(item.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.changed = true;
      this.notify.ok('Pago eliminado correctamente');
      if (this.editing && this.editing.uid === item.uid) this.cancelEdit();
      this.reloadList();
    });
  }

  public itemConcept(item: { concepto: string }): string {
    return stripFinancingConcept(item.concepto, this.financiacionPrivada.descripcion);
  }

  public closeDialog(): void {
    if (this.saving) return;
    this.dialogRef.close(this.changed);
  }

  public trackByUid(_: number, item: PagoCuotasFinanciacionPrivadaModel): string {
    return item.uid;
  }

  private openRequestedEdit(): void {
    if (!this.data.editUid || this.requestedEditDone) return;
    this.requestedEditDone = true;
    const item = this.dataSource.find(entry => entry.uid === this.data.editUid);
    if (item) this.startEdit(item);
  }

  private load(): void {
    this.ready = false;
    Promise.all([
      this.loadActivities(),
      this.loadBankAccounts(),
      this.loadTaxes(),
      this.financiacionPrivadaService.pagoCoutasList(this.financiacionPrivada.uid),
    ])
      .then(([activities, bankAccounts, taxes, list]) => {
        this.activities = activities;
        this.bankAccounts = bankAccounts;
        this.taxes = taxes;
        this.dataSource = list || [];
        this.resetForm();
        this.openRequestedEdit();
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.ready = true; });
  }

  private reloadList(): void {
    this.financiacionPrivadaService.pagoCoutasList(this.financiacionPrivada.uid)
      .then(list => { this.dataSource = list || []; })
      .catch(() => this.notify.error('No se pudo recargar el listado de pagos'));
    this.financiacionPrivadaService.get(this.financiacionPrivada.uid)
      .then(financiacion => { if (financiacion) this.pendingAmount = Number(financiacion.importePendiente) || 0; })
      .catch(() => { });
  }

  private loadActivities(): Promise<EntityFormOption[]> {
    return this.activityService.getList()
      .then(list => (list || [])
        .filter(activity => activity && activity.uid && activity.uid !== consts.EMPTY_GUID)
        .map(activity => ({ id: activity.uid, display: activity.name || '' })))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadBankAccounts(): Promise<EntityFormOption[]> {
    return firstValueFrom(this.masterService.getBankAccounts())
      .then(list => bankAccountOptions(list))
      .catch(() => [] as EntityFormOption[]);
  }

  private loadTaxes(): Promise<EntityFormOption[]> {
    return this.preinvoiceService.accountsAndTaxesData()
      .then(accountsAndTaxes => [NO_TAX_OPTION].concat(((accountsAndTaxes && accountsAndTaxes.taxes) || [])
        .filter(tax => tax.name === 'IRPF 19% Operaciones de capitalización')
        .map(tax => ({ id: tax.id, display: tax.name || '' }))))
      .catch(() => [NO_TAX_OPTION]);
  }

  private findTax(taxesId: any): EntityFormOption {
    const match = this.taxes.find(tax => String(tax.id) === String(taxesId));
    return match || NO_TAX_OPTION;
  }

  private resetForm(): void {
    this.injected.clear();
    this.submitted = false;
    this.saveError = '';
    this.model = new PagoCuotasFinanciacionPrivadaModel();
    this.model.taxesId = 0;
    this.form.reset({
      fecha: null,
      bankAccount: null,
      concepto: '',
      importeCapital: null,
      intereses: 0,
      gastosBancarios: 0,
      tax: NO_TAX_OPTION,
      activity: null,
    }, { emitEvent: false });
    this.applySingleOptionDefaults();
  }

  private applySingleOptionDefaults(): void {
    if (this.editing) return;

    if (this.missingActivity) {
      const current = currentActivityOption(this.activities);
      if (current) this.control('activity').setValue(current, { emitEvent: false });
      else if (this.activities.length === 1) this.control('activity').setValue(this.activities[0], { emitEvent: false });
    }
    if (this.missingBankAccount && this.bankAccounts.length === 1) {
      this.control('bankAccount').setValue(this.bankAccounts[0], { emitEvent: false });
    }
  }

  private buildPayload(): PagoCuotasFinanciacionPrivadaModel {
    const value = this.form.getRawValue();
    const payload = { ...this.model } as PagoCuotasFinanciacionPrivadaModel;
    payload.financiacionPrivadaId = this.financiacionPrivada.uid;
    payload.fecha = value.fecha;
    payload.concepto = String(value.concepto || '').trim();
    payload.importeCapital = Number(value.importeCapital) || 0;
    payload.intereses = this.optionalAmount(value.intereses);
    payload.gastosBancarios = this.optionalAmount(value.gastosBancarios);
    payload.taxesId = Number(optionId(value.tax)) || 0;
    payload.activitiesUid = optionId(value.activity);
    payload.bankAccountsUid = optionId(value.bankAccount);
    return payload;
  }

  private setSaving(saving: boolean, message = ''): void {
    this.saving = saving;
    this.savingMessage = message;
    this.dialogRef.disableClose = saving;
  }

  private itemDetails(item: PagoCuotasFinanciacionPrivadaModel): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [
      { label: 'Capital', value: Utils.FormatMoney(Number(item.importeCapital) || 0) + ' €' },
      { label: 'Intereses', value: Utils.FormatMoney(Number(item.intereses) || 0) + ' €' },
    ];
    if (item.fecha) details.push({ label: 'Fecha', value: formatDate(item.fecha, 'dd/MM/yyyy', 'es-ES') });
    if (item.bankAccountInfo) details.push({ label: 'Cuenta bancaria', value: item.bankAccountInfo });
    return details;
  }

  private optionalAmount(value: any): number {
    return value === null || value === undefined || value === '' ? 0 : Number(value) || 0;
  }

  private serverMessage(error: any): string {
    return (error && error.error && error.error.message) || '';
  }
}
